// File: AccountingFilters.tsx
import React from 'react';
import { FaFilter, FaCalendarAlt, FaTag, FaMoneyBillWave, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { AccountingCategory } from '../../../firebase/accountingFirestoreService';

interface AccountingFiltersProps {
  filters: {
    status: string[];
    category: string[];
    paymentMethod: string[];
    dateFrom: string;
    dateTo: string;
    minAmount: number | '';
    maxAmount: number | '';
  };
  setFilters: React.Dispatch<React.SetStateAction<any>>;
  categories: AccountingCategory[];
}

const AccountingFilters: React.FC<AccountingFiltersProps> = ({
  filters,
  setFilters,
  categories
}) => {
  const statusOptions = ['Pending', 'Approved', 'Paid', 'Rejected'];
  const paymentMethodOptions = ['Cash', 'Bank Transfer', 'Credit Card', 'Mobile Money', 'Cheque', 'Other'];

  const handleStatusChange = (status: string) => {
    setFilters((prev: { status: string[]; }) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s: string) => s !== status)
        : [...prev.status, status]
    }));
  };

  const handleCategoryChange = (category: string) => {
    setFilters((prev: { category: string[]; }) => ({
      ...prev,
      category: prev.category.includes(category)
        ? prev.category.filter((c: string) => c !== category)
        : [...prev.category, category]
    }));
  };

  const handlePaymentMethodChange = (method: string) => {
    setFilters((prev: { paymentMethod: string[]; }) => ({
      ...prev,
      paymentMethod: prev.paymentMethod.includes(method)
        ? prev.paymentMethod.filter((m: string) => m !== method)
        : [...prev.paymentMethod, method]
    }));
  };

  const handleReset = () => {
    setFilters({
      status: [],
      category: [],
      paymentMethod: [],
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: ''
    });
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FaFilter className="h-4 w-4 text-gray-500" />
          <h3 className="text-sm font-medium text-gray-900">Filters</h3>
        </div>
        <button
          onClick={handleReset}
          className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 hover:bg-gray-200 rounded"
        >
          Reset All
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-7 gap-4 items-end">
        {/* Status */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Status</label>
          <select
            value={filters.status[0] || ''}
            onChange={(e) => setFilters((prev: any) => ({
              ...prev,
              status: e.target.value ? [e.target.value] : []
            }))}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All</option>
            {statusOptions.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        
        {/* Category */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Categories</label>
          <select
            value={filters.category[0] || ''}
            onChange={(e) => setFilters((prev: any) => ({
              ...prev,
              category: e.target.value ? [e.target.value] : []
            }))}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Payment Methods</label>
          <select
            value={filters.paymentMethod[0] || ''}
            onChange={(e) => setFilters((prev: any) => ({
              ...prev,
              paymentMethod: e.target.value ? [e.target.value] : []
            }))}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All</option>
            {paymentMethodOptions.map(pm => (
              <option key={pm} value={pm}>{pm}</option>
            ))}
          </select>
        </div>

        {/* From Date */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">From Date</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <FaCalendarAlt className="h-3 w-3 text-gray-400" />
            </div>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters((prev: any) => ({ ...prev, dateFrom: e.target.value }))}
              className="pl-9 w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* To Date */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">To Date</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <FaCalendarAlt className="h-3 w-3 text-gray-400" />
            </div>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters((prev: any) => ({ ...prev, dateTo: e.target.value }))}
              className="pl-9 w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Min Amount */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Min Amount</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <FaMoneyBillWave className="h-3 w-3 text-gray-400" />
            </div>
            <input
              type="number"
              value={filters.minAmount}
              onChange={(e) => setFilters((prev: any) => ({
                ...prev,
                minAmount: e.target.value === '' ? '' : Number(e.target.value)
              }))}
              placeholder="0.00"
              className="pl-9 w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Max Amount */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Max Amount</label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <FaMoneyBillWave className="h-3 w-3 text-gray-400" />
            </div>
            <input
              type="number"
              value={filters.maxAmount}
              onChange={(e) => setFilters((prev: any) => ({
                ...prev,
                maxAmount: e.target.value === '' ? '' : Number(e.target.value)
              }))}
              placeholder="0.00"
              className="pl-9 w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

      </div>
    </div>
  );
};

export default AccountingFilters;